import _extends from "@babel/runtime/helpers/extends";
import { getUtility, SimpleModal } from '@livable/saas-react-components';
import { Button, TextField } from '@mui/material';
import { DataGrid, GridColumnMenu } from '@mui/x-data-grid';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'src/stores';
import { exportCSVFromDataGrid } from 'src/utils/exportCSV';
import useStyles from './styles';
const PreviewCycleChargesModal = observer(_ref => {
  var _property$allocations, _cycleChargesPreview$;
  let disabled = _ref.disabled;
  const classes = useStyles();
  const store = useStore();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const currentCompany = store.homeDashboardStore.getCurrentCompany || {};
  const previewCycleCharges = currentCompany.billingTab.previewCycleCharges || {};
  const isOpen = currentCompany.billingTab.isPreviewCycleChargesModalOpen;
  const property = store.homeDashboardStore.getPropertyById(previewCycleCharges.propertyId);
  const cycleChargesPreview = (property === null || property === void 0 || (_property$allocations = property.allocations) === null || _property$allocations === void 0 ? void 0 : _property$allocations.cycleChargesPreviewByAllocationId.get(previewCycleCharges.allocationCycleId + previewCycleCharges.propertyId)) || [];
  const utilitiesIds = ((_cycleChargesPreview$ = cycleChargesPreview[0]) === null || _cycleChargesPreview$ === void 0 ? void 0 : _cycleChargesPreview$.utilityItems.map(item => item.utilityId)) || [];
  const renderCell = (parameter, left) => /*#__PURE__*/React.createElement("span", {
    className: "".concat(classes.cellContent, " ").concat(left && classes.cellLeft)
  }, parameter);
  const renderHeader = (text, right) => /*#__PURE__*/React.createElement("span", {
    className: right && classes.rightAlign
  }, text);
  const columns = [{
    align: 'right',
    flex: 1,
    field: 'unitName',
    headerName: t('allocations.unit_name'),
    isExportable: true,
    maxWidth: 120,
    minWidth: 85,
    hideable: false,
    renderCell: params => renderCell(params.row.unitName, true),
    renderHeader: () => renderHeader(t('allocations.unit_name')),
    sortComparator: (v1, v2) => {
      var _ref2;
      return (_ref2 = '' + v1) === null || _ref2 === void 0 ? void 0 : _ref2.localeCompare(v2, undefined, {
        numeric: true
      });
    },
    valueGetter: params => params.row.unitName
  }, {
    align: 'right',
    field: 'residentName',
    flex: 1,
    headerName: 'Resident Name',
    isExportable: true,
    maxWidth: 300,
    minWidth: 210,
    renderCell: params => {
      if (params.row.residentAccountId) {
        return renderCell(store.account.profilesFullName.get(params.row.residentAccountId), true);
      } else {
        return renderCell(t('allocations.not_enrolled'), true);
      }
    },
    renderHeader: () => renderHeader('Resident \nName'),
    valueGetter: params => {
      if (params.row.residentAccountId) {
        return store.account.profilesFullName.get(params.row.residentAccountId);
      } else {
        return t('allocations.not_enrolled');
      }
    }
  }];
  utilitiesIds.forEach(utilityId => {
    const utilityProviderData = store.utilityProviders.utilities.get(utilityId);
    const utilityData = getUtility(utilityProviderData === null || utilityProviderData === void 0 ? void 0 : utilityProviderData.utilityTypeName);
    columns.push({
      align: 'right',
      field: utilityId,
      flex: 1,
      headerName: utilityData.title,
      isExportable: true,
      maxWidth: 300,
      minWidth: 80,
      renderCell: params => {
        const utility = params.row.utilityItems.find(item => item.utilityId === utilityId);
        return renderCell('$' + utility.amount.toFixed(2), true);
      },
      renderHeader: () => renderHeader(utilityData.title),
      valueGetter: params => {
        const utility = params.row.utilityItems.find(item => item.utilityId === utilityId);
        return utility ? utility.amount.toFixed(2) : 'N/A';
      }
    });
  });
  columns.push({
    align: 'right',
    field: 'totalAmount',
    flex: 1,
    headerName: 'Total',
    isExportable: true,
    headerAlign: 'right',
    minWidth: 100,
    renderCell: params => renderCell('$' + params.row.totalAmount.toFixed(2)),
    renderHeader: () => renderHeader('Total', true),
    valueGetter: params => params.row.totalAmount.toFixed(2)
  });
  const handleDownloadCsv = () => {
    exportCSVFromDataGrid({
      columns,
      name: 'allocation_totals.csv',
      rows: cycleChargesPreview
    });
  };
  const CustomColumnMenu = props => {
    return /*#__PURE__*/React.createElement(GridColumnMenu, _extends({}, props, {
      slots: {
        columnMenuUserItem: () => /*#__PURE__*/React.createElement("div", {
          className: classes.exportCSV
        }, /*#__PURE__*/React.createElement(Button, {
          onClick: handleDownloadCsv,
          size: "small",
          variant: "contained"
        }, "Export CSV"))
      },
      slotProps: {
        columnMenuUserItem: {
          displayOrder: 15
        }
      }
    }));
  };
  return /*#__PURE__*/React.createElement(SimpleModal, {
    disabledTrigger: disabled,
    handleClose: () => {
      currentCompany.billingTab.setIsPreviewCycleChargesModalOpen(false);
      currentCompany.billingTab.clearPreviewCycleCharges();
    },
    noPadding: true,
    isOpen: isOpen,
    title: 'Allocation totals ' + previewCycleCharges.cycleName,
    subTitle: "".concat(previewCycleCharges.buildingAddress || '', " "),
    variant: "middle"
  }, /*#__PURE__*/React.createElement(DataGrid, {
    columns: columns,
    getRowId: row => row.unitId + row.residentAccountId,
    pagination: true,
    disableRowSelectionOnClick: true,
    autoHeight: true,
    columnHeaderHeight: 80,
    rowHeight: 60,
    sx: {
      overflowX: 'scroll'
    },
    hideFooterSelectedRowCount: true,
    getRowClassName: () => classes.row,
    initialState: {
      pagination: {
        paginationModel: {
          pageSize: 25,
          page: 0
        }
      },
      sorting: {
        sortModel: [{
          field: 'unitName',
          sort: 'asc'
        }]
      }
    },
    slots: {
      columnMenu: CustomColumnMenu,
      ColumnMenuIcon: () => /*#__PURE__*/React.createElement("span", null),
      BaseButton: props => /*#__PURE__*/React.createElement(Button, _extends({}, props, {
        variant: "actionButton",
        'custom-attribute': '119-allocation-preview-cycle-charges'
      })),
      BaseTextField: props => /*#__PURE__*/React.createElement(TextField, _extends({}, props, {
        variant: "outlined"
      }))
    },
    slotProps: {
      filterPanel: {
        filterFormProps: {
          columnInputProps: {
            variant: 'outlined'
          },
          operatorInputProps: {
            variant: 'outlined'
          }
        }
      }
    },
    rows: cycleChargesPreview
  }));
});
export default PreviewCycleChargesModal;